/**
 * RSA-OAEP encryption utility for encrypting credentials before sending to backend.
 * Uses the Web Crypto API (RSA-OAEP / SHA-256). Requires a secure context
 * (HTTPS or http://localhost) — `window.crypto.subtle` is undefined otherwise.
 */
import { getEnv } from "./env";

// Fail-fast: the credential flow (login / reset / change) is mandatory, so a
// misconfigured deploy without the public key should crash at module load
// rather than silently degrade to "encryption error" toasts at runtime.
if (!getEnv("REACT_APP_RSA_PUBLIC_KEY")) {
  throw new Error("REACT_APP_RSA_PUBLIC_KEY is not set.");
}

function base64ToArrayBuffer(base64) {
  const binaryString = window.atob(base64);
  const bytes = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes.buffer;
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";

  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return window.btoa(binary);
}

function isWebCryptoAvailable() {
  return (
    typeof window !== "undefined" &&
    window.isSecureContext &&
    window.crypto &&
    window.crypto.subtle
  );
}

export async function encryptWithPublicKey(plainText, publicKeyBase64) {
  if (!isWebCryptoAvailable()) {
    throw new Error("Web Crypto unavailable in this insecure context.");
  }

  const publicKeyBuffer = base64ToArrayBuffer(publicKeyBase64);

  const publicKey = await window.crypto.subtle.importKey(
    "spki",
    publicKeyBuffer,
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    false,
    ["encrypt"]
  );

  const encodedText = new TextEncoder().encode(plainText);

  const encryptedBuffer = await window.crypto.subtle.encrypt(
    {
      name: "RSA-OAEP",
    },
    publicKey,
    encodedText
  );

  return arrayBufferToBase64(encryptedBuffer);
}

/**
 * Convenience wrapper that encrypts using the public key from environment variables.
 * @param {string} plainText - The plaintext to encrypt
 * @returns {Promise<string>} Base64-encoded encrypted string
 */
export async function encryptPassword(plainText) {
  const publicKey = getEnv("REACT_APP_RSA_PUBLIC_KEY");
  if (!publicKey) {
    throw new Error("RSA public key not configured.");
  }
  return encryptWithPublicKey(plainText, publicKey);
}
